/**
 * Horizontal tab bar for the Home page forms table: the permanent "My View"
 * tab, the configured report tabs with their count tags, the icon-only view
 * menu, and one tab per saved view. Rendered by `HomePageToolbar`; every action
 * goes to the orchestrator.
 *
 * "My View" is a destination, never a command: always first, always enabled,
 * labelled with its own document's name. That document is deliberately omitted
 * from the saved-view tabs — it owns a reserved tab, it is not duplicated. The
 * sliders item is the contextual view menu for whichever tab is active: it acts
 * on `activeSavedViewId`, is a command so it never enters `selectedKeys`, and
 * nothing in it is ever hidden — only disabled. It sits where the canonical
 * reports end and the user's own views begin, ahead of the saved-view tabs that
 * antd's overflow collapses first.
 *
 * `flex: '1 1 0'` is load-bearing. antd's overflow absolutely-positions the
 * items it collapses, so a content-sized basis measures only what is already
 * visible and collapses the bar to a single tab. The zero basis gives it a
 * definite width to measure against; the `minWidth` floor stays under that
 * growth so it never pins.
 *
 * `maxWidth` is what makes the bar collapse at all. Without a cap the bar grows
 * to whatever the row has spare, so every saved view fits and the tabs sprawl
 * across the toolbar. The cap fits the reserved tabs plus a few saved views;
 * past that antd's overflow takes over. `HomePageToolbar` pins its right-hand
 * controls to the row's edge, because the bar no longer claims the free width.
 */
import { FC } from 'react';
import { Menu, MenuProps, Tag, Tooltip, Typography, theme } from 'antd';
import Icon from '../../../atoms/Icon';
import type { HomePageOrchestrator } from '../HomePageContent/useHomePageOrchestrator';

const { Text } = Typography;

export type TableFilterViewId = string | 'favorites' | undefined;

export interface TableFilterButtonsProps {
  orchestrator: HomePageOrchestrator;
  activeViewId: TableFilterViewId;
  reports: Array<{ id: string; label: string }>;
}

const TableFilterButtons: FC<TableFilterButtonsProps> = ({
  orchestrator: o,
  activeViewId,
  reports,
}) => {
  const { token } = theme.useToken();

  const selectedKeys: string[] = (() => {
    if (activeViewId === 'myView') return ['my-view'];
    if (activeViewId === 'favorites') {
      return o.selectedCustomViewId ? [`custom-${o.selectedCustomViewId}`] : [];
    }
    if (activeViewId && reports.some((report) => report.id === activeViewId)) return [activeViewId];
    return [];
  })();

  const myViewItem: MenuProps['items'] = [
    {
      key: 'my-view',
      label: (
        <Text><Icon.Home shift={3} /> {o.myViewLabel}</Text>
      ),
    },
  ];

  const reportItems: MenuProps['items'] = reports.map((report) => {
    const reportCount = o.reportCounts?.[report.id];
    return {
      key: report.id,
      label: reportCount !== undefined ? (
        <Text>{report.label} <Tag style={{ borderRadius: 100 }}>{reportCount}</Tag></Text>
      ) : report.label,
    };
  });

  const isReportActive = o.activeViewKind === 'report';
  const hasActiveSavedView = Boolean(o.activeSavedViewId);

  const viewMenuItem: MenuProps['items'] = [
    {
      key: 'view-menu',
      label: (
        <Tooltip title="View options">
          <span aria-label="View options"><Icon.Sliders /></span>
        </Tooltip>
      ),
      children: [
        {
          key: 'active-view-group',
          type: 'group',
          label: o.activeViewName,
          children: [
            { key: 'save-view', label: 'Save', disabled: isReportActive },
            { key: 'save-as-new-view', label: 'Save as new view…' },
            { key: 'rename-view', label: 'Rename…', disabled: isReportActive || !hasActiveSavedView },
            { key: 'reset-view', label: 'Reset' },
          ],
        },
        {
          key: 'table-group',
          type: 'group',
          label: 'Table',
          children: [
            { key: 'insert-column', label: 'Insert column…' },
            { key: 'clear-all-filters', label: 'Clear all filters' },
            { key: 'export-table', label: 'Export table' },
          ],
        },
        {
          key: 'view-menu-divider',
          type: 'divider',
        },
        {
          key: 'set-as-my-view',
          label: 'Set as My View',
          disabled: o.activeViewKind !== 'savedView',
        },
        {
          key: 'delete-view',
          label: 'Delete view',
          danger: true,
          disabled: isReportActive || !hasActiveSavedView,
        },
      ],
    },
  ];

  const savedViewItems: MenuProps['items'] = o.customViews
    .filter((view) => view.id !== o.myViewTargetId)
    .map((view) => ({
      key: `custom-${view.id}`,
      label: view.name,
    }));

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'save-view':
        void o.handleSaveActiveView();
        return;
      case 'save-as-new-view':
        o.openSaveAsNewViewModal();
        return;
      case 'rename-view':
        o.handleRenameActiveView();
        return;
      case 'reset-view':
        o.handleResetActiveView();
        return;
      case 'insert-column':
        o.handleAddColumn();
        return;
      case 'clear-all-filters':
        o.handleClearAllFilters();
        return;
      case 'export-table':
        void o.handleExportTable();
        return;
      case 'set-as-my-view':
        o.handleSetActiveViewAsMyView();
        return;
      case 'delete-view':
        o.handleDeleteActiveView();
        return;
      case 'my-view':
        o.handleSelectMyView();
        return;
      default:
        break;
    }
    if (reports.some((r) => r.id === key)) {
      o.handleViewChange(key);
      return;
    }
    if (key.startsWith('custom-')) {
      o.handleCustomViewSelect(key.replace('custom-', ''));
    }
  };

  return (
    <Menu
      mode="horizontal"
      selectedKeys={selectedKeys}
      onClick={handleClick}
      items={[
        ...myViewItem,
        ...reportItems,
        ...viewMenuItem,
        ...savedViewItems,
      ]}
      style={{
        background: 'transparent',
        flex: '1 1 0',
        minWidth: token.controlHeight * 6,
        maxWidth: token.controlHeight * 22,
        borderBottom: 'none',
      }}
    />
  );
};

export default TableFilterButtons;
